import React, { memo } from 'react';
import { Col, Row, Table, TableProps } from 'antd';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { formatTipDate } from 'Reports/utils';
import {
  TipPerTransactionReport,
  TipPerTransactionReportParams,
} from 'utils/apiClient/services/reports/types';

interface Props extends TableProps<TipPerTransactionReport> {
  currency: string;
  reportData: Array<TipPerTransactionReport> | undefined;
  handleTableChange: (t: string) => void;
  tableParams: TipPerTransactionReportParams;
  isDataLoading: boolean;
}

const TipAmount = ({
  amount,
  currency,
}: {
  amount: number | null;
  currency: string;
}) => (
  <>
    {amount !== null ? (
      <FormattedNumber
        style="currency" // eslint-disable-line react/style-prop-object
        currency={currency}
        value={amount}
      />
    ) : (
      <span>-</span>
    )}
  </>
);

const TipDate = ({ record }: { record: TipPerTransactionReport }) => (
  <>
    <span>{formatTipDate(record.time_added)}</span>
  </>
);

const TipPerTransactionTable = ({
  currency,
  reportData,
  handleTableChange,
  tableParams,
  isDataLoading,
  ...props
}: Props) => (
  <Row>
    <Col span={24}>
      <Table<TipPerTransactionReport>
        css={`
          border-width: 1px 1px 1px 1px;
          border-style: solid;
          border-radius: 1px;
          border-color: rgba(0, 0, 0, 0.06);
          margin-top: 20px;
          .ant-table-placeholder {
            min-height: calc(100vh - 500px);
          }
        `}
        dataSource={reportData}
        rowKey={({ id }) => id}
        size="middle"
        loading={isDataLoading}
        {...props}
      >
        <Table.Column<TipPerTransactionReport>
          key="time_added"
          width="15%"
          title={<FormattedMessage defaultMessage="Date" />}
          render={(record: TipPerTransactionReport) => (
            <TipDate record={record} />
          )}
        />
        <Table.Column<TipPerTransactionReport>
          key="invoice_number"
          width="15%"
          title={<FormattedMessage defaultMessage="Invoice" />}
          dataIndex="invoice_number"
        />
        <Table.Column<TipPerTransactionReport>
          key="user_name"
          width="20%"
          title={<FormattedMessage defaultMessage="Employee" />}
          dataIndex="user_name"
        />
        <Table.Column<TipPerTransactionReport>
          key="amount"
          width="20%"
          title={<FormattedMessage defaultMessage="Tip" />}
          dataIndex="amount"
          render={(amount: number) => (
            <TipAmount amount={amount} currency={currency} />
          )}
        />
        <Table.Column<TipPerTransactionReport>
          key="tax_rate"
          width="20%"
          title={<FormattedMessage defaultMessage="Tip tax rate" />}
          render={(data: TipPerTransactionReport) =>
            data.tax_rate !== null ? (
              <span>
                <FormattedNumber
                  maximumFractionDigits={2}
                  value={parseFloat(data.tax_rate.toString())}
                />
                %
              </span>
            ) : (
              '-'
            )
          }
        />
        <Table.Column<TipPerTransactionReport>
          key="tax_amount"
          width="20%"
          title={<FormattedMessage defaultMessage="Tax amount" />}
          dataIndex="tax_amount"
          render={(amount: number) => (
            <TipAmount amount={amount} currency={currency} />
          )}
        />
      </Table>
    </Col>
  </Row>
);

export default memo(TipPerTransactionTable);
